import React, { useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  StepConnector,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  UploadFile,
  AddLocationAlt,
  AddCard,
  Payment,
} from "@mui/icons-material";
import { AmenitiesSection } from "./components/AmenitiesSection";
import { SiteRoadwayInformationSection } from "./components/SiteRoadways";
import DashboardHeader from "@components/Header/DashboardHeader";
import GenericBreadCrums from "@components/Breadcrumbs";
import UploadDocument from "./components/UploadDocuments";
// import PaymentSection from "./components/Payment";
import MapIcon from "@mui/icons-material/Map";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "@store/store";
import type { RootReducerType } from "@store/store";
import { setCoordinatesAccess } from "./redux/amenitesSlice";
import MapPanel from "./components/MapPanel";
import PostAddOutlinedIcon from "@mui/icons-material/PostAddOutlined";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import ScrollToTopButton from "../../../src/utils/scrollToTopButton";
import { SiteRoadwaysAccess } from "./components/SiteRoadWaysAccess";
import { useGeolocation } from "@gis/components/useGeoLocation";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import UnderTakingConsent from "./components/UnderTakingConsent";
import { useNavigate } from "react-router-dom";
import PaymentSection from "./components/PaymentNew";
import { resetApplicationSubmitted } from "@modules/application-section/application-details/redux/applicationSubmittedSlice";

const steps = [
  { label: "Amenities Location Details", icon: <AddLocationAlt /> },
  { label: "Site & Highways Information", icon: <PostAddOutlinedIcon /> },
  { label: "Upload Documents", icon: <UploadFileRoundedIcon /> },
  { label: "Application Preview", icon: <VisibilityIcon /> },
  { label: "Undertakings & Consents", icon: <ReceiptLongIcon /> },
  { label: "Payment", icon: <Payment /> },
];

const CustomStepConnector = styled(StepConnector)(() => ({
  [`& .MuiStepConnector-line`]: {
    display: "none",
  },
}));

const CustomStepIcon: React.FC<any> = (props) => {
  const { active, completed, icon } = props;

  const stepIconIndex = Number(icon) - 1;
  const iconClass = completed
    ? "step-icon completed"
    : active
      ? "step-icon active"
      : "step-icon";

  return (
    <div className="step-icon-wrapper">
      <div className={iconClass}>{steps[stepIconIndex]?.icon}</div>
      <span
        className={`step-label ${icon === 6 ? "" : "text-with-line"}`}
        style={{
          maxWidth: "100px",
          whiteSpace: "normal",
          display: "inline-block",
          lineHeight: "1.4",
        }}
      >
        {steps[stepIconIndex]?.label}
      </span>
    </div>
  );
};

const StepperComponent: React.FC = () => {
  const {
    coordinates: geoCoordinates,
    error,
    isLoading,
  } = useGeolocation({
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  });

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [isProcessing, setIsProcessing] = useState(false);
  const {
    latitude,
    longitude,
    start_chainage_km,
    start_chainage_m,
    end_chainage_km,
    end_chainage_m,
    start_chainage,
    end_chainage,
    chainage,
    isFirstSectionCompleted,
  } = useSelector((state: RootReducerType) => state.amenities);

  const startChainageText =
    start_chainage_km && start_chainage_m
      ? `${start_chainage_km}+${start_chainage_m}`
      : start_chainage || "";
  const endChainageText =
    end_chainage_km && end_chainage_m
      ? `${end_chainage_km}+${end_chainage_m}`
      : end_chainage || "";
  const chainageDisplay = startChainageText
    ? endChainageText
      ? `${startChainageText}`
      : startChainageText
    : chainage || "";
  const isApplicationSubmitted = useSelector(
    (state: RootReducerType) =>
      state.isApplicationSubmitted?.isApplicationSubmitted,
  );
  const dashboard = useSelector((state: any) => state.applicationDashboard);

  const { activeStep } = useSelector((state: RootReducerType) => state.stepper);
  const [isSidePanelCollapsed, setIsSidePanelCollapsed] = useState(false);

  // Use state to manage coordinates
  const [coordinates, setCoordinates] = useState({
    latitude: latitude ?? 0,
    longitude: longitude ?? 0,
  });

  const toggleSidePanel = () => {
    setIsSidePanelCollapsed((prev) => !prev);
  };

  React.useEffect(() => {
    if (activeStep === 3) {
      dispatch(resetApplicationSubmitted());
      navigate("/application-preview");
      return;
    }
    if (activeStep > 0) {
      setIsSidePanelCollapsed(true);
    } else {
      setIsSidePanelCollapsed(false);
    }
  }, [activeStep]);

  const handleMapClick = React.useCallback(
    (coords: any) => {
      dispatch(
        setCoordinatesAccess({
          latitude: coords.latitude ?? "",
          longitude: coords.longitude ?? "",
        }),
      );

      // Update local coordinates state
      setCoordinates({
        latitude: coords.latitude ?? 0,
        longitude: coords.longitude ?? 0,
      });
    },
    [dispatch],
  );

  React.useEffect(() => {
    if (isApplicationSubmitted) {
      navigate(`/application-details`, { replace: true });
    }
  }, []);

  React.useEffect(() => {
    // Update coordinates when latitude or longitude from the store changes
    setCoordinates({
      latitude: latitude ?? 0,
      longitude: longitude ?? 0,
    });
  }, [latitude, longitude]);

  if (!dashboard) {
    return null;
  }
  const { application_code } = dashboard;

  const breadcrumbData = [
    {
      label: application_code ? application_code : "New Application",
      link: "#",
    },
  ];

  return (
    <div className="page-content">
      {/* <DashboardHeader />/ */}
      <GenericBreadCrums breadcrumbs={breadcrumbData} className="mt-6" />
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={2}
        sx={{
          marginTop: "1rem",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ fontSize: { xs: "14px", md: "18px" } }}
        >
          Access&nbsp;Permission
        </Typography>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: "none", sm: "block" } }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            overflowX: "auto",
          }}
        >
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor:
                    activeStep >= index
                      ? "rgba(58, 28, 115, 0.24)"
                      : "rgba(0, 0, 0, 0.05)",
                  color:
                    activeStep >= index
                      ? "rgba(58, 28, 115, 1)"
                      : "rgba(117, 117, 117, 1)",
                  border:
                    activeStep === index
                      ? "1px solid rgba(58, 28, 115, 1)"
                      : "1px solid rgba(0, 0, 0, 0.08)",
                  padding: { xs: "4px 4px", sm: "5px 6px" },
                  borderRadius: "25px",
                  minWidth: "fit-content",
                  transition: "all 0.3s ease",
                  mb: { xs: 1, sm: 0 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    backgroundColor:
                      activeStep >= index
                        ? "rgba(58, 28, 115, 1)"
                        : "rgba(0, 0, 0, 0.08)",
                    color: activeStep >= index ? "#fff" : "#757575",
                    fontSize: "10px",
                    fontWeight: 700,
                  }}
                >
                  {index + 1}
                </Box>
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    textAlign: { xs: "center", sm: "left" },
                  }}
                >
                  {step.label}
                </Typography>
              </Box>
              {index < steps.length - 1 && (
                <Box
                  sx={{
                    width: "20px",
                    height: "1px",
                    backgroundColor: "#d0d0d0",
                    mx: 0.5,
                    display: { xs: "none", sm: "block" },
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
      <Box className="stepper-container">
        <Box className="step-body-wrapper">
          <Box
            className="step-body mx-auto position-relative"
            sx={{ flexDirection: { xs: "column", sm: "row" } }}
          >
            <Box className="left-panel">
              {activeStep === 0 && (
                <AmenitiesSection collapsed={isSidePanelCollapsed} />
              )}
              {activeStep === 1 && (
                <SiteRoadwaysAccess collapsed={isSidePanelCollapsed} />
              )}
              {activeStep === 2 && (
                <UploadDocument collapsed={isSidePanelCollapsed} />
              )}
              {activeStep === 4 && (
                <UnderTakingConsent collapsed={isSidePanelCollapsed} />
              )}
              {activeStep === 5 && (
                <PaymentSection isProcessing={isProcessing} />
              )}
            </Box>
            {!isSidePanelCollapsed && (
              <MapPanel
                hideRemovePin={false}
                geoCoordinates={geoCoordinates}
                coordinates={coordinates}
                chainages={chainageDisplay}
                onMapClick={handleMapClick}
                isFirstSectionCompleted={isFirstSectionCompleted}
              />
            )}
          </Box>
          {false && (
            <Box className="toggle-map" onClick={toggleSidePanel}>
              {isSidePanelCollapsed ? (
                <MapIcon className="map-toggle-icon" />
              ) : (
                <CloseIcon className="map-toggle-icon" />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default StepperComponent;
